import React from 'react';
import filter from 'lodash/filter';
import first from 'lodash/first';
import isEmpty from 'lodash/isEmpty';
import { stringToBoolean } from '../../utilities/conversionUtility';
import {
  interpolatePriceToTemplate,
  isDefaultTitle,
  priceFormat,
  parseInt,
} from '../../utilities/helperUtility';
import Translation from '../../utilities/localizationUtility';
import dayjs from '../../../dayjs/dayjs_timezone';

export const checkVariantsAvailability = (orderVariants, attrs, available, capacityType) => {
  if (isEmpty(orderVariants) || isEmpty(attrs)) {
    return true;
  }

  if (capacityType === 1) {
    return Object.values(orderVariants).every((v) => (
      !v.quantity || attrs[v.external_id].max >= (v.quantity * v.unit_multiplier)
    ));
  }
  const quantity = Object.values(orderVariants).reduce((sum, v) => sum + (v.quantity * v.unit_multiplier), 0);

  return quantity <= available;
};

export const checkVariantsAvailabilityForQuantity = ({
  attrs,
  id,
  quantity,
  available,
  capacityType,
  orderVariants,
  variants,
  setting,
}) => {
  const minimumQuantity = stringToBoolean(setting.set_minimum_quantity) ? setting.minimum_quantity : 0;
  let validMinQuantity = true;

  if (isEmpty(attrs)) {
    return true;
  }

  if (isEmpty(orderVariants)) {
    return available >= quantity;
  }

  if (capacityType === 1) {
    return Object.values(orderVariants).every((v) => {
      const newQuantity = parseInt(v.external_id) === parseInt(id) ? quantity : v.quantity;
      return !newQuantity || attrs[v.external_id].max >= (newQuantity * v.unit_multiplier);
    });
  }
  const sumQuantity = Object.values(orderVariants).reduce((sum, v) => {
    const newQuantity = parseInt(v.external_id) === parseInt(id) ? quantity : v.quantity;
    return sum + (newQuantity * v.unit_multiplier);
  }, 0);

  if (variants.length === 1 && minimumQuantity) {
    validMinQuantity = sumQuantity >= minimumQuantity;
  }

  return validMinQuantity && sumQuantity <= available;
};

export const getVariantsFilteredBySeason = (props) => {
  // should use timezone of store to correctly filter by season
  const {
    variants,
    hasSeasonalRates,
    seasonalRates,
    selectedTimeslot,
    timeZone,
  } = props;
  const selectedDateObj = selectedTimeslot ? dayjs(selectedTimeslot).tz(timeZone).startOf('day') : null;

  if (!variants || !hasSeasonalRates) {
    return variants || [];
  }

  // Do not display variant if date is not selected
  if (!selectedDateObj) {
    return [];
  }

  const filteredSeasonalRates = seasonalRates.filter((seasonalRate) => {
    const srStart = dayjs.tz(seasonalRate.starts_at, timeZone).startOf('day');
    const srFinish = dayjs.tz(seasonalRate.finishes_at, timeZone).endOf('day');

    if (seasonalRate.recurring) {
      const selectedYear = selectedDateObj.year();
      const isSameOrAfter = selectedDateObj.isSameOrAfter(srStart.year(selectedYear));
      const isSameOrBefore = selectedDateObj.isSameOrBefore(srFinish.year(selectedYear));
      return isSameOrAfter && isSameOrBefore;
    }

    return selectedDateObj.isSameOrAfter(srStart)
      && selectedDateObj.isSameOrBefore(srFinish);
  });

  return variants.filter((variant) => (
    filteredSeasonalRates.some((seasonalRate) => (
      seasonalRate.season === variant.options[0]
        && seasonalRate.rate === variant.options[1]
    ))
  ));
};

export const getColumns = (props) => {
  const { productOptions, setting, variants } = props;
  const onlyDefaultTitle = variants && variants.length === 1 && isDefaultTitle(variants[0].title);
  const headers = productOptions ? productOptions.map((x) => x.name) : [];
  const isShowPrice = Object.prototype.hasOwnProperty.call(setting, 'show_price')
    ? stringToBoolean(setting.show_price)
    : true;
  const t = new Translation(props);

  if (!onlyDefaultTitle && isShowPrice) {
    headers.push(t.key('columnPrice'));
  }

  // prepare the list from variant.options and according headers ordering
  const lists = getVariantsFilteredBySeason(props).map((variant) => {
    const hash = { key: variant.external_id };
    const extra = Array.from(headers).slice(variant.options.length);
    variant.options.filter((x) => x).forEach((option, index) => (hash[headers[index]] = option));

    if (isShowPrice) {
      extra.forEach((x) => {
        const value = variant[x === t.key('columnPrice') ? 'price' : x.toLowerCase()];
        hash[x] = (
          <span
            dangerouslySetInnerHTML={{
              __html: interpolatePriceToTemplate(priceFormat(props), value),
            }}
          />
        );
      });
    }

    return hash;
  });

  if (onlyDefaultTitle) {
    lists[0].Title = t.key('columnQuantity');
  }

  return { headers, lists };
};

export const getFirstBookableDate = (frames, availableDates, options = {}) => {
  const { month } = options;
  const dates = frames.map((x) => x.date);
  const today = new Date();
  const firstBookableDate = first(filter(dates, (x) => {
    const date = dayjs(x);
    return date.diff(today, 'days') >= 0 && (!month || date.isSame(month, 'month'));
  }));

  if (firstBookableDate) {
    if (!availableDates.includes(firstBookableDate)) {
      return availableDates[0];
    }

    return firstBookableDate;
  }

  return null;
};
